import { useSelector } from 'react-redux';
import { useMyZoneSelectors } from 'features/my-zone/store/selectors';
import { useSelectorWithProps } from 'utils/structured-selector/structured-selector';
import { useMyZonePermissions } from './use-my-zone-permissions';
import { useMemo } from 'react';
import { ReportTypes } from 'core/constants/report-type';

export const useIsLoading = () => {
    const { selectIsLoadingContent } = useMyZoneSelectors();
    return useSelector(selectIsLoadingContent);
};

export const useAllCreatedPOIs = () => {
    const { selectAllCreatedPOIs } = useMyZoneSelectors();
    return useSelector(selectAllCreatedPOIs);
};

export const useIsRefetchingPlaceTagData = () => {
    const { selectIsRefetchingPlaceTagData } = useMyZoneSelectors();
    return useSelector(selectIsRefetchingPlaceTagData);
};

export const useIsInitialPlaceTagDataLoadingDone = () => {
    const { selectIsInitialPlaceTagDataLoadingDone } = useMyZoneSelectors();
    return useSelector(selectIsInitialPlaceTagDataLoadingDone);
};

export const usePlacesForTag = () => {
    const { selectPlacesForTags } = useMyZoneSelectors();
    return useSelector(selectPlacesForTags);
};

export const useMyTagComparisonDates = () => {
    const { selectMyTagsComparisonDates } = useMyZoneSelectors();
    return useSelector(selectMyTagsComparisonDates);
};

export const usePortfolioComparisonDates = () => {
    const { selectPortfolioComparisonDates } = useMyZoneSelectors();
    return useSelector(selectPortfolioComparisonDates);
};

export const useRecentEntities = (reportTypes: ReportTypes[]) => {
    const { selectAllRecentEntities } = useMyZoneSelectors();
    return useSelectorWithProps(
        {
            reportTypes,
        },
        selectAllRecentEntities,
    );
};

export const useVenueSuggestions = () => {
    const { selectVenueSuggestions } = useMyZoneSelectors();
    return useSelector(selectVenueSuggestions);
};

export const useIsVenueSuggestionsLoading = () => {
    const { selectIsVenueSuggestionsLoading } = useMyZoneSelectors();
    return useSelector(selectIsVenueSuggestionsLoading);
};

export const useIsCreatingTag = () => {
    const { selectIsCreatingTag } = useMyZoneSelectors();
    return useSelector(selectIsCreatingTag);
};

export const useIsUpdatingTag = () => {
    const { selectIsUpdatingTag } = useMyZoneSelectors();
    return useSelector(selectIsUpdatingTag);
};

export const usePlacesForTagWithoutComparisonData = () => {
    const { selectPlacesForTagWithoutComparisonData } = useMyZoneSelectors();
    return useSelector(selectPlacesForTagWithoutComparisonData);
};

export const useIsPlacesForTagLoading = () => {
    const { selectIsPlacesForTagLoading } = useMyZoneSelectors();
    return useSelector(selectIsPlacesForTagLoading);
};
export const useIsPlacesForTagError = () => {
    const { selectIsPlacesForTagError } = useMyZoneSelectors();
    return useSelector(selectIsPlacesForTagError);
};

export const useAllGroups = () => {
    const { selectGroups } = useMyZoneSelectors();
    return useSelector(selectGroups);
};

export const useGroup = (groupId: string) => {
    const { selectGroups } = useMyZoneSelectors();
    const allGroups = useSelector(selectGroups);
    return allGroups.find((group) => group.id === groupId);
};

export const useReportTypes = () => {
    const { hasEnableBillboardsPermission } = useMyZonePermissions();
    return useMemo((): ReportTypes[] => {
        let reportTypes: ReportTypes[] = ['Property', 'Chain'];

        if (hasEnableBillboardsPermission) {
            reportTypes.push('Billboard');
        }

        return reportTypes;
    }, [hasEnableBillboardsPermission]);
};

export const usePortfolioProperties = () => {
    const { getPortfolioProperties } = useMyZoneSelectors();
    return useSelector(getPortfolioProperties);
};

export const useTagsProperties = () => {
    const { getTagsProperties } = useMyZoneSelectors();
    return useSelector(getTagsProperties);
};
